<template>
  <Button
    :class="`button-box-decide ${computedClassBlur}`"
    data-testid="button-box-decide"
    :colors="buttonBoxColors"
    :href="href"
    :disabled="disabled"
    :loading="loading"
    v-on="$listeners"
  >
    <span class="content">
      <span class="section">
        <BaseIcon :icon="icon" :class="`icon icon--${computedClass}`" />
        <span
          v-if="badgeLabel"
          :class="`badge-label badge-label--${computedClass}`"
        >
          {{ badgeLabel }}
        </span>
      </span>
      <span :class="`label label--${computedClass}`">
        <slot name="label" :class="`label--${computedClass}`">
          <!-- @slot para inserir componente customizado na label -->
          {{ label }}
        </slot>
      </span>
      <span :class="`secondary-text secondary-text--${computedClass}`">
        {{ secondaryText }}
      </span>
    </span>
  </Button>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import Button from '@/foundation/button/Button.vue';
import BaseIcon from '@/foundation/base-icon/BaseIcon.vue';
import { buttonBoxColors, buttonBoxBlurColors } from '@/styles/Button';

@Component({
  name: 'ButtonBoxDecide',
  components: { Button, BaseIcon }
})
export default class ButtonBoxDecide extends Vue {
  /**
   *  Desativa o botão
   */
  @Prop({ type: Boolean, default: false })
  readonly disabled!: boolean;

  /**
   * Coloca o botão no estado "carregando"
   */
  @Prop({ type: Boolean, default: false })
  readonly loading!: boolean;

  /**
   * O ícone a ser exibido
   */
  @Prop({ type: String, required: true })
  readonly icon!: string;

  /**
   * O título do botão
   */
  @Prop({ type: String })
  readonly label?: string;

  /**
   * Permite atribuir um link ao botão
   */
  @Prop({ type: String })
  readonly href?: string;

  /**
   * Descrição do botão
   */
  @Prop({ type: String })
  readonly secondaryText?: string;

  /**
   * A badge com texto adicional
   */
  @Prop({ type: String })
  readonly badgeLabel?: string;

  /**
   *  Coloca o botão com estilo do tipo blur
   */
  @Prop({ type: Boolean, default: false })
  readonly isBlur!: boolean;

  readonly buttonBoxColors = this.isBlur
    ? buttonBoxBlurColors
    : buttonBoxColors;

  private get computedClass(): string {
    return this.disabled ? 'disabled' : '';
  }

  private get computedClassBlur(): string {
    return this.isBlur ? `blur blur--${this.computedClass}` : '';
  }
}
</script>

<style lang="less" scoped>
.button-box-decide {
  background: @base-primary;
  width: 100%;
  padding: @size-spacing-x500;
  border: @size-border-x400 solid @divider-primary;
  border-radius: @size-radius-x300;

  &.blur {
    backdrop-filter: blur(5px) saturate(1.4) brightness(1.01);
    background: rgba(255, 255, 255, 0.1);
    border: 0;

    &:hover {
      transition: all ease 0.5s;
      transform: none;
    }

    &--disabled {
      opacity: 0.5;
    }

    .label,
    .icon {
      color: @text-over-image;
    }
  }
}

.content {
  width: 100%;
  display: flex;
  flex-direction: column;
  text-align: left;
}

.icon {
  padding: @size-spacing-x150;
  color: @element-primary;
  &--disabled {
    color: @base-disabled;
  }
}

.section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: @size-spacing-x600;
}

.badge-label {
  .text-p-6();
  padding: 0 @size-spacing-x300;
  border-radius: @size-radius-x100;
  color: @element-primary;
  background-color: @theme-secondary;

  &--disabled {
    color: @base-over-disabled;
    background-color: @base-disabled;
  }
}

.label {
  .text-p-4();
  color: @text-primary;
  font-weight: 600;
  &--disabled,
  &--disabled > :deep(*) {
    color: @base-disabled !important;
  }
}

.secondary-text {
  .text-p-5();
  margin-top: @size-spacing-x300;
  color: @element-secondary;
  &--disabled {
    color: @base-disabled;
  }
}
</style>
